import { useEffect, useState } from 'react'
import AIDocumentAssistant from '../components/AIDocumentAssistant'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const STATUS_COLORS = { draft: 'neutral', sent: 'info', accepted: 'success', declined: 'danger' }
const STATUS_LABELS = { draft: 'Draft', sent: 'Sent', accepted: 'Accepted ✓', declined: 'Declined' }

const PRESET_SERVICES = [
  { name: 'Federal Individual Income Tax Return (Form 1040)', description: 'Preparation and filing of federal return', fee: '' },
  { name: 'State Income Tax Return', description: 'Preparation and filing of state return', fee: '' },
  { name: 'S-Corporation Federal Tax Return (Form 1120S)', description: 'Preparation and filing of 1120S with K-1s', fee: '' },
  { name: 'Partnership Federal Tax Return (Form 1065)', description: 'Preparation and filing of 1065 with K-1s', fee: '' },
  { name: 'C-Corporation Federal Tax Return (Form 1120)', description: 'Preparation and filing of 1120', fee: '' },
  { name: 'Bookkeeping Services (Monthly)', description: 'Monthly reconciliation and financial statements', fee: '' },
  { name: 'Payroll Processing', description: 'Bi-weekly payroll processing and tax filings', fee: '' },
  { name: 'Tax Planning Consultation', description: 'Year-round strategic tax planning', fee: '' },
  { name: 'QuickBooks Setup & Training', description: 'Initial setup and staff training', fee: '' },
  { name: 'IRS Correspondence Assistance', description: 'Respond to IRS notices on your behalf', fee: '' },
]

export default function ProposalBuilderPage() {
  const { profile } = useAuth()
  const [clients, setClients] = useState([])
  const [proposals, setProposals] = useState([])
  const [tab, setTab] = useState('create') // 'create' | 'history'
  const [clientType, setClientType] = useState('existing') // 'existing' | 'prospect'
  const [selectedClient, setSelectedClient] = useState('')
  const [prospectName, setProspectName] = useState('')
  const [prospectEmail, setProspectEmail] = useState('')
  const [services, setServices] = useState([{ name: '', description: '', fee: '' }])
  const [taxYear, setTaxYear] = useState(new Date().getFullYear())
  const [introMessage, setIntroMessage] = useState('Thank you for the opportunity to present this proposal. We look forward to helping you achieve your financial goals and making tax season stress-free.')
  const [expiryDate, setExpiryDate] = useState('')
  const [generating, setGenerating] = useState(false)
  const [generated, setGenerated] = useState(null)
  const [error, setError] = useState('')
  const [showPresets, setShowPresets] = useState(false)

  useEffect(() => {
    supabase.from('clients').select('id, full_name, business_name, entity_type')
      .eq('cpa_id', profile.id).order('business_name')
      .then(({ data }) => setClients(data || []))
    loadProposals()
  }, [])

  const [converting, setConverting] = useState(null)
  const [showAI, setShowAI] = useState(false)

  function applyAIData(data) {
    if (data.clientName) {
      setClientType('prospect')
      setProspectName(data.clientName)
      if (data.clientEmail) setProspectEmail(data.clientEmail)
    }
    if (data.taxYear) setTaxYear(data.taxYear)
    if (data.services?.length) {
      setServices(data.services.map(s => ({
        name: s.name || '',
        description: s.description || '',
        fee: s.fee || ''
      })))
    }
    if (data.introMessage) setIntroMessage(data.introMessage)
    if (data.expiryDays) {
      const d = new Date()
      d.setDate(d.getDate() + data.expiryDays)
      setExpiryDate(d.toISOString().split('T')[0])
    }
  }

  async function convertToClient(proposal) {
    setConverting(proposal.id)
    try {
      const { data: existing } = await supabase
        .from('clients')
        .select('id')
        .eq('email', proposal.prospect_email)
        .eq('cpa_id', profile.id)
        .maybeSingle()

      if (existing) {
        alert('A client with this email already exists!')
        setConverting(null)
        return
      }

      const { data: newClient, error } = await supabase.from('clients').insert({
        cpa_id: profile.id,
        full_name: proposal.prospect_name,
        business_name: proposal.prospect_name,
        email: proposal.prospect_email || '',
        entity_type: '1040',
        status: 'not_started',
        tax_year: proposal.tax_year || new Date().getFullYear(),
      }).select().single()

      if (error) { alert('Error creating client: ' + error.message); setConverting(null); return }

      // Create organizer
      await supabase.from('organizers').insert({
        client_id: newClient.id,
        tax_year: proposal.tax_year || new Date().getFullYear(),
        entity_type: '1040',
        status: 'not_started'
      })

      // Link proposal to client
      await supabase.from('proposals').update({ client_id: newClient.id, status: 'accepted' }).eq('id', proposal.id)
      setProposals(prev => prev.map(p => p.id === proposal.id ? { ...p, client_id: newClient.id, status: 'accepted' } : p))

      alert(`✅ ${proposal.prospect_name} has been added as a client! Go to the Clients page to send their portal invite.`)
    } catch (err) {
      alert('Error: ' + err.message)
    }
    setConverting(null)
  }

  async function loadProposals() {
    const { data } = await supabase.from('proposals').select('*').order('created_at', { ascending: false }).limit(50)
    setProposals(data || [])
  }

  function addService() { setServices(prev => [...prev, { name: '', description: '', fee: '' }]) }
  function removeService(i) { setServices(prev => prev.filter((_, idx) => idx !== i)) }
  function updateService(i, field, val) { setServices(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: val } : s)) }

  function addPreset(preset) {
    setServices(prev => [...prev.filter(s => s.name), { ...preset }])
    setShowPresets(false)
  }

  const subtotal = services.reduce((s, x) => s + (parseFloat(x.fee) || 0), 0)

  async function handleGenerate() {
    if (clientType === 'existing' && !selectedClient) { setError('Please select a client'); return }
    if (clientType === 'prospect' && !prospectName) { setError('Please enter prospect name'); return }
    setGenerating(true); setError(''); setGenerated(null)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/.netlify/functions/generate-proposal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          client_id: clientType === 'existing' ? selectedClient : null,
          prospect_name: clientType === 'prospect' ? prospectName : null,
          prospect_email: clientType === 'prospect' ? prospectEmail : null,
          services: services.filter(s => s.name),
          tax_year: taxYear,
          intro_message: introMessage,
          expiry_date: expiryDate || null,
        })
      })
      const data = await res.json()
      if (data.error) { setError(data.error); setGenerating(false); return }
      setGenerated(data)
      loadProposals()
    } catch (err) {
      setError(err.message)
    }
    setGenerating(false)
  }

  function printProposal() {
    if (!generated?.html) return
    const html = atob(generated.html)
    const w = window.open('', '_blank')
    w.document.write(html)
    w.document.close()
    w.focus()
    setTimeout(() => w.print(), 500)
  }

  async function updateProposalStatus(id, status) {
    await supabase.from('proposals').update({ status }).eq('id', id)
    setProposals(prev => prev.map(p => p.id === id ? { ...p, status } : p))
  }

  return (
    <div>
      {/* Header */}
      <div className="card" style={{ padding: '24px 28px', marginBottom: 20, background: '#202326', border: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ color: '#fff', fontSize: 20, marginBottom: 4 }}>Proposal Builder</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, margin: 0 }}>Create professional service proposals for clients and prospects.</p>
          </div>
          <button onClick={() => setShowAI(true)} style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', color: '#CBB17B', padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            🤖 Generate with AI
          </button>
          <div style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 8, padding: '8px 14px', fontSize: 12, color: '#CBB17B', fontWeight: 600 }}>
            {proposals.length} Proposals
          </div>
        </div>
      </div>

      {showAI && (
        <AIDocumentAssistant
          type="proposal"
          clients={clients}
          onApply={applyAIData}
          onClose={() => setShowAI(false)}
        />
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', background: '#F7F3EB', borderRadius: 10, padding: 4, marginBottom: 20, gap: 4, maxWidth: 360 }}>
        {[['create', '✏️ Create Proposal'], ['history', '📋 Proposal History']].map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)} style={{
            flex: 1, padding: '8px', borderRadius: 8, border: 'none',
            fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
            background: tab === t ? '#202326' : 'transparent',
            color: tab === t ? '#CBB17B' : '#62615D'
          }}>{label}</button>
        ))}
      </div>

      {tab === 'create' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* Left column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Client/Prospect */}
            <div className="card" style={{ padding: '20px 22px' }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Who is this for?</h3>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                {[['existing', '👥 Existing Client'], ['prospect', '✨ New Prospect']].map(([t, label]) => (
                  <button key={t} onClick={() => setClientType(t)} style={{
                    flex: 1, padding: '8px', borderRadius: 8, border: '2px solid',
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    borderColor: clientType === t ? '#CBB17B' : '#DED6CA',
                    background: clientType === t ? '#F1E8D7' : '#fff',
                    color: clientType === t ? '#765B2C' : '#62615D'
                  }}>{label}</button>
                ))}
              </div>

              {clientType === 'existing' ? (
                <div>
                  <label className="label">Select Client</label>
                  <select className="input" value={selectedClient} onChange={e => setSelectedClient(e.target.value)}>
                    <option value="">Choose a client…</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.business_name || c.full_name}</option>)}
                  </select>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 12 }}>
                  <div>
                    <label className="label">Prospect Name *</label>
                    <input className="input" placeholder="Business or individual name" value={prospectName} onChange={e => setProspectName(e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Prospect Email</label>
                    <input className="input" type="email" placeholder="email@example.com" value={prospectEmail} onChange={e => setProspectEmail(e.target.value)} />
                  </div>
                </div>
              )}

              <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="label">Tax Year</label>
                  <input className="input" type="number" value={taxYear} onChange={e => setTaxYear(parseInt(e.target.value))} min="2020" max="2030" />
                </div>
                <div>
                  <label className="label">Proposal Expires</label>
                  <input className="input" type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} />
                </div>
              </div>
            </div>

            {/* Intro message */}
            <div className="card" style={{ padding: '20px 22px' }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Introduction Message</h3>
              <textarea className="input" rows={4} value={introMessage} onChange={e => setIntroMessage(e.target.value)} style={{ resize: 'vertical', fontSize: 13 }} />
            </div>
          </div>

          {/* Right column - Services */}
          <div className="card" style={{ padding: '20px 22px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700 }}>Services & Pricing</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setShowPresets(!showPresets)}>
                  📋 Presets
                </button>
                <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={addService}>+ Add</button>
              </div>
            </div>

            {/* Preset picker */}
            {showPresets && (
              <div style={{ background: '#F7F3EB', borderRadius: 8, padding: '10px', marginBottom: 12, maxHeight: 160, overflow: 'auto' }}>
                {PRESET_SERVICES.map((p, i) => (
                  <div key={i} onClick={() => addPreset(p)} style={{ padding: '6px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#202326' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#E8E2D9'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    {p.name}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
              {services.map((s, i) => (
                <div key={i} style={{ background: '#FAFAF8', borderRadius: 8, padding: '10px 12px', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                    <input className="input" placeholder="Service name *" value={s.name} onChange={e => updateService(i, 'name', e.target.value)} style={{ flex: 1, fontSize: 12 }} />
                    <input className="input" placeholder="Fee $" value={s.fee} onChange={e => updateService(i, 'fee', e.target.value)} style={{ width: 80, fontSize: 12 }} />
                    <button onClick={() => removeService(i)} style={{ background: 'none', border: 'none', color: '#F87171', cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>×</button>
                  </div>
                  <input className="input" placeholder="Brief description (optional)" value={s.description || ''} onChange={e => updateService(i, 'description', e.target.value)} style={{ fontSize: 11, background: '#fff' }} />
                </div>
              ))}
            </div>

            {/* Subtotal */}
            <div style={{ borderTop: '2px solid #CBB17B', paddingTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#202326' }}>Total Proposal Value</span>
              <span style={{ fontSize: 18, fontWeight: 800, color: '#CBB17B' }}>${subtotal.toFixed(2)}</span>
            </div>
          </div>

          {/* Generate button - full width */}
          <div style={{ gridColumn: '1 / -1' }}>
            {error && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>{error}</div>}

            {generated ? (
              <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 10, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ fontSize: 32 }}>🎉</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#065F46', marginBottom: 4 }}>Proposal generated successfully!</div>
                  <div style={{ fontSize: 12, color: '#62615D' }}>Saved to proposal history. Print or save as PDF to share with your client.</div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-primary" onClick={printProposal}>🖨️ Print / Save PDF</button>
                  <button className="btn btn-ghost" onClick={() => { setGenerated(null); setServices([{ name: '', description: '', fee: '' }]) }}>New Proposal</button>
                </div>
              </div>
            ) : (
              <button className="btn btn-primary" onClick={handleGenerate} disabled={generating}
                style={{ width: '100%', justifyContent: 'center', padding: '14px', fontSize: 15 }}>
                {generating ? (
                  <><span style={{ width: 16, height: 16, border: '2px solid rgba(201,168,76,0.3)', borderTopColor: '#CBB17B', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} /> Generating…</>
                ) : '📄 Generate Proposal'}
              </button>
            )}
          </div>
        </div>
      ) : (
        /* Proposal History */
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {proposals.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
              <h3 style={{ marginBottom: 8 }}>No proposals yet</h3>
              <p style={{ color: '#62615D', fontSize: 13 }}>Create your first proposal using the builder.</p>
            </div>
          ) : (
            proposals.map((p, i) => (
              <div key={p.id} style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 14, borderBottom: i < proposals.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ width: 42, height: 42, borderRadius: 10, background: '#F1E8D7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>📄</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#202326' }}>{p.prospect_name}</div>
                  <div style={{ fontSize: 11, color: '#62615D' }}>
                    {p.proposal_number} · Tax Year {p.tax_year} · ${(p.total_amount || 0).toFixed(2)}
                    {p.expiry_date && ` · Expires ${new Date(p.expiry_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                  </div>
                </div>
                <span className={`badge badge-${STATUS_COLORS[p.status]}`}>{STATUS_LABELS[p.status]}</span>
                <select
                  value={p.status}
                  onChange={e => updateProposalStatus(p.id, e.target.value)}
                  style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid #DED6CA', cursor: 'pointer', background: '#fff' }}
                  onClick={e => e.stopPropagation()}>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                {(p.status === 'accepted' || p.status === 'sent') && !p.client_id && (
                  <button
                    className="btn btn-primary"
                    style={{ fontSize: 11, padding: '4px 12px', flexShrink: 0 }}
                    disabled={converting === p.id}
                    onClick={() => convertToClient(p)}>
                    {converting === p.id ? '…' : '👤 Convert to Client'}
                  </button>
                )}
                {p.client_id && (
                  <span style={{ fontSize: 10, color: '#10B981', fontWeight: 700, flexShrink: 0 }}>✅ Client Added</span>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}
